import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  Slide,
  SlideProps,
  IconButton,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Error,
  Warning,
  Info,
  ContentCopy
} from '@mui/icons-material';

// Notification types
export interface NotificationOptions {
  message: string;
  type?: AlertColor;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  persistent?: boolean;
  copyable?: boolean;
  details?: string;
}

interface Notification extends NotificationOptions {
  id: string;
  timestamp: number;
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (options: NotificationOptions) => string;
  hideNotification: (id: string) => void;
  clearAllNotifications: () => void;
  // Convenience methods
  showSuccess: (message: string, options?: Partial<NotificationOptions>) => string;
  showError: (message: string, options?: Partial<NotificationOptions>) => string;
  showWarning: (message: string, options?: Partial<NotificationOptions>) => string;
  showInfo: (message: string, options?: Partial<NotificationOptions>) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Slide transition component
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

// Hook for using notifications
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new (Error as any)('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Provider component
interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 5
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = () => {
    // Use crypto for better randomness (even for UI IDs)
    const array = new Uint8Array(6);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(36)).join('').slice(0, 9);
  };

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showNotification = useCallback((options: NotificationOptions): string => {
    const id = generateId();
    const notification: Notification = {
      id,
      timestamp: Date.now(),
      type: 'info',
      duration: 6000,
      persistent: false,
      copyable: false,
      ...options
    };

    setNotifications(prev => {
      // Remove oldest notifications if we exceed the max
      const newNotifications = [notification, ...prev];
      return newNotifications.slice(0, maxNotifications);
    });

    // Auto-hide non-persistent notifications
    if (!notification.persistent && notification.duration && notification.duration > 0) {
      setTimeout(() => {
        hideNotification(id);
      }, notification.duration);
    }

    return id;
  }, [maxNotifications, hideNotification]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({ message, type: 'success', ...options });
  }, [showNotification]);

  const showError = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({
      message,
      type: 'error',
      persistent: true,
      copyable: true,
      ...options
    });
  }, [showNotification]);

  const showWarning = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({ message, type: 'warning', duration: 8000, ...options });
  }, [showNotification]);

  const showInfo = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({ message, type: 'info', ...options });
  }, [showNotification]);

  const getIcon = (type: AlertColor) => {
    switch (type) {
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <Error />;
      case 'warning':
        return <Warning />;
      case 'info':
      default:
        return <Info />;
    }
  };

  const handleCopy = (message: string, details?: string) => {
    const textToCopy = details ? `${message}\n\nDetalji:\n${details}` : message;
    navigator.clipboard.writeText(textToCopy);
    showSuccess('Tekst je kopiran!', { duration: 2000 });
  };

  const value: NotificationContextType = React.useMemo(() => ({
    notifications,
    showNotification,
    hideNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }), [notifications, showNotification, hideNotification, clearAllNotifications, showSuccess, showError, showWarning, showInfo]);

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Render notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={SlideTransition}
          sx={{
            mb: index * 7, // Stack notifications
            '& .MuiSnackbar-root': {
              position: 'relative'
            }
          }}
        >
          <Alert
            severity={notification.type}
            icon={getIcon(notification.type || 'info')}
            variant="filled"
            sx={{
              minWidth: 350,
              maxWidth: 500,
              boxShadow: 3,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            action={
              <Box display="flex" alignItems="center" gap={1}>
                {notification.copyable && (
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(notification.message, notification.details)}
                    sx={{ color: 'inherit' }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                )}

                {notification.action && (
                  <Chip
                    label={notification.action.label}
                    onClick={notification.action.onClick}
                    variant="outlined"
                    size="small"
                    sx={{
                      color: 'inherit',
                      borderColor: 'currentColor',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  />
                )}

                <IconButton
                  size="small"
                  onClick={() => hideNotification(notification.id)}
                  sx={{ color: 'inherit' }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {notification.message}
              </Typography>

              {notification.details && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                  {notification.details}
                </Typography>
              )}

              {notification.persistent && (
                <Chip
                  label="Trajna poruka"
                  size="small"
                  variant="outlined"
                  sx={{
                    mt: 1,
                    height: 20,
                    fontSize: '0.7rem',
                    color: 'inherit',
                    borderColor: 'currentColor'
                  }}
                />
              )}
            </Box>
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};
